import Icon from "components/ui/Icon";
import LoadingDots from "components/ui/LoadingDots";
import React from "react";
import {
  LinkProps as BaseRouterLinkProps,
  Link as RouterLink,
} from "react-router-dom";
import styled, { css } from "styled-components";

export enum ButtonSize {
  xs = "xs",
  sm = "sm",
  md = "md",
  lg = "lg",
  xl = "xl",
}

export enum ButtonLevel {
  primary = "primary",
  secondary = "secondary",
  tertiary = "tertiary",
  cta = "cta",
  danger = "danger",
  ghost = "ghost",
  success = "success",
}

interface StyledProps {
  $isLoading?: boolean;
  $isSuccess?: boolean;
  $fullWidth?: boolean;
  $size: ButtonSize;
  $level: ButtonLevel;
  theme: any; // TODO: Replace with correct type
}

const getSizeTheme = ({ $size, theme }) => {
  switch ($size) {
    case ButtonSize.xl:
      return `
        padding: 8px 48px;
        height: 60px;
        font-size: ${theme.fontSizesUI.xl}px;
      `;
    case ButtonSize.lg:
      return `
        padding: 8px 36px;
        height: 52px;
        font-size: ${theme.fontSizesUI.lg}px;
      `;
    case ButtonSize.md:
      return `
        padding: 8px 24px;
        height: 44px;
        font-size: ${theme.fontSizesUI.md}px;
      `;
    case ButtonSize.sm:
      return `
        padding: 4px 20px;
        height: 36px;
        font-size: ${theme.fontSizesUI.sm}px;
      `;
    case ButtonSize.xs:
      return `
        padding: 4px 8px;
        height: 28px;
        font-size: ${theme.fontSizesUI.sm}px;
      `;
    default:
      return "";
  }
};

const getLevelTheme = ({ $level, theme }) => {
  switch ($level) {
    // TODO: Rethink level names
    case ButtonLevel.primary:
      return `
        --background: ${theme.colors.text};
        border: none;
        --color: ${theme.reverseColors.text};
      `;
    case ButtonLevel.secondary:
      return `
        --background: ${theme.colors.secondary};
        border: none;
        --color: ${theme.reverseColors.secondary};
      `;
    case ButtonLevel.tertiary:
      return `
        --background: ${theme.colors.tertiary};
        border: none;
        --color: ${theme.reverseColors.tertiary};
      `;
    case ButtonLevel.cta:
      return `
        --background: ${theme.colors.primary};
        border: none;
        --color: ${theme.reverseColors.primary};
      `;
    case ButtonLevel.danger:
      return `
        --background: ${theme.colors.danger};
        border: none;
        --color: ${theme.reverseColors.danger};
      `;
    case ButtonLevel.ghost:
      return `
        --background: transparent;
        border: 1.5px solid ${theme.colors.primary}40;
        --color: ${theme.colors.text};
      `;
    default:
      return "";
  }
};

const getTheme = (props: StyledProps) => `
    ${getSizeTheme(props)}
    ${getLevelTheme(props)}
  `;

export const BaseButton = styled.button`
  padding: 0;

  appearance: none;
  cursor: pointer;
  border: 0;
  background: none;
  color: inherit;

  font-family: ${(props) => props.theme.fonts.body};
  text-align: center;
`;

const Loading = styled(LoadingDots)<{ size: ButtonSize }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  ${(props) =>
    [ButtonSize.xs, ButtonSize.sm].includes(props.size) &&
    css`
      transform: scale(0.5) translate(-50%, -50%);
      transform-origin: center;
    `};
`;

const Success = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    height: 1em;
    width: 1em;

    stroke: currentColor;
    stroke-width: 1em;
  }
`;

const Content = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const StyledButton = styled(BaseButton)<StyledProps>`
  position: relative;

  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;

  border-radius: ${(props) => props.theme.borderRadius.button}px;
  color: var(--color);
  background: var(--background);

  font-weight: ${(props) => props.theme.fontWeights.bold};
  text-decoration: none;

  transition: ${(props) => props.theme.transitions.simple};
  transition-property: background, color;

  ${(props) => getTheme(props)}

  ${(props) =>
    !props.$isSuccess &&
    css`
      &[disabled] {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
      }
    `}

  &:visited {
    color: var(--color);
  }

  @media (hover: hover) {
    &:hover {
      filter: brightness(1.2);
    }
  }

  ${(props) =>
    props.$isLoading
      ? css`
          opacity: 0.5;
          cursor: not-allowed;
          pointer-events: none;

          ${Content} {
            visibility: hidden;
            opacity: 0;
          }
        `
      : ""}

  ${(props) =>
    props.$isSuccess
      ? css`
          --color: ${(props) => props.theme.reverseColors.success};
          background: ${(props) => props.theme.colors.success};
          border: none;
          cursor: not-allowed;
          pointer-events: none;

          ${Content} {
            visibility: hidden;
            opacity: 0;
          }
        `
      : ""}

  ${(props) =>
    props.$fullWidth
      ? `
  width: 100%;
  `
      : ""}
`;

interface BaseProps {
  as?: React.ElementType;
  isLoading?: boolean;
  isSuccess?: boolean;
  successMessage?: string;
  fullWidth?: boolean;
  size?: ButtonSize;
  level?: ButtonLevel;
  disabled?: boolean;
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    BaseProps {}

export interface LinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>,
    BaseProps {}

export interface RouterLinkProps extends BaseRouterLinkProps, BaseProps {}

const RootButton: React.FC<
  BaseProps & { component?: string | React.FC<any>; children?: any }
> = ({
  isLoading,
  isSuccess,
  successMessage,
  fullWidth,
  disabled,
  children,
  size = ButtonSize.lg,
  level = ButtonLevel.primary,
  component,
  ...props
}) => (
  <StyledButton
    as={component}
    disabled={isLoading || disabled}
    $isLoading={isLoading}
    $isSuccess={isSuccess}
    $fullWidth={fullWidth}
    $size={size}
    $level={level}
    {...props}
  >
    {isLoading && <Loading size={size} />}
    {isSuccess && (
      <Success>
        <Icon name="checkmark" />
        {successMessage}
      </Success>
    )}
    <Content>{children}</Content>
  </StyledButton>
);

const Button: React.FC<ButtonProps> = (props) => <RootButton {...props} />;

export const ButtonLink: React.FC<LinkProps> = (props) => (
  <RootButton component="a" data-turbo="false" {...props} />
);

export const ButtonRouterLink: React.FC<RouterLinkProps> = (props) => (
  <RootButton component={RouterLink} data-turbo="false" {...props} />
);

export default Button;
